/** @jsx jsx */
import { jsx } from "@emotion/react";
import { Grid } from "@material-ui/core";
import { FC, useCallback, useMemo } from "react";
import { RowSelectionTableDataProps } from "src/hooks/table/useSelectedRows";
import { useFormatMsg } from "src/hooks/useFormatMsg";

import { FlowioInputType } from "../../../globalTypes";
import { AddButton } from "../../atoms/inputs/EwaButtons";
import FlowioInput from "../../atoms/inputs/FlowioInput";
import FlowioSelect from "../../atoms/inputs/FlowioSelect";
import { Option } from "../../atoms/inputs/FlowioSelect/types";
import { MoreActionsBtnType } from "../../pages/Administration/types";
import { PAGING_OPTIONS } from "../TemplateView/fragments/TemplateTable/Controls/constants";

import { DataActionsContainer, inputCss } from "./styles";

import UserFilterToggleButton from "../../atoms/inputs/EwaButtons/UserFilterToggleButton";
import { DesktopOnly } from "../../atoms/ViewportRestrictor";
import { getPagingOption } from "./utils";

const TableControls: FC<DesktopAndTabletControlsProps> = ({
  pagingOptions = PAGING_OPTIONS,
  onChangeOfRowsPerPage,
  rowsPerPage,
  rowSelectionProps,
  allowRowInsertion,
  toggleRowInsertionModal,
  fulltextKeyword,
  onFulltextChange,
  toggleAdvancedSearchUI,
  focusFullTextSearch = false,
  MoreActionsBtn,
  moreBtnClickCallback,
}) => {
  const formatMsg = useFormatMsg();

  const handleSearchChange = useCallback(
    (optionVal: string) => {
      onChangeOfRowsPerPage(Number(optionVal));
    },
    [onChangeOfRowsPerPage]
  );

  const pagingValue = useMemo(
    () => getPagingOption(pagingOptions, rowsPerPage),
    [pagingOptions, rowsPerPage]
  );

  return (
    <DataActionsContainer>
      <Grid container spacing={1} alignItems="center">
        {allowRowInsertion && (
          <Grid item>
            <AddButton onClick={toggleRowInsertionModal} />
          </Grid>
        )}

        {toggleAdvancedSearchUI !== null && (
          <Grid item>
            <UserFilterToggleButton onClick={toggleAdvancedSearchUI} />
          </Grid>
        )}

        {MoreActionsBtn && rowSelectionProps && (
          <MoreActionsBtn
            afterSubmitCallback={moreBtnClickCallback}
            selectedRowsProps={rowSelectionProps}
          />
        )}

        <Grid item data-cy="search-field">
          <FlowioInput
            type={FlowioInputType.Text}
            value={fulltextKeyword ?? ""}
            onChange={onFulltextChange}
            placeholder={formatMsg("common.fulltext.search")}
            css={inputCss}
            autoFocus={focusFullTextSearch}
          />
        </Grid>
      </Grid>

      <Grid item>
        <DesktopOnly>
          <FlowioSelect
            options={pagingOptions}
            onChange={handleSearchChange}
            value={pagingValue}
          />
        </DesktopOnly>
      </Grid>
    </DataActionsContainer>
  );
};

export interface DesktopAndTabletControlsProps {
  allowRowInsertion: boolean;
  toggleRowInsertionModal: () => void;
  rowSelectionProps?: RowSelectionTableDataProps;

  pagingOptions?: Option[];
  onChangeOfRowsPerPage: (newRowsPerPage: number) => void;

  rowsPerPage: number | null;
  MoreActionsBtn?: MoreActionsBtnType;
  moreBtnClickCallback(): void;

  fulltextKeyword: string | undefined;
  onFulltextChange: (newValue: string) => void;
  focusFullTextSearch?: boolean;
  toggleAdvancedSearchUI: (() => void) | null;
}

export default TableControls;
